import { TFunction } from 'i18next'
import { FormState } from 'react-hook-form'
import { ApiResult } from 'src/Libs/api'
import { InstallationRequestBatch } from 'src/Libs/flow/advanced-settings/components'
import { Meter } from 'src/Libs/flow/advanced-settings/powermeter'
import { updateEvse } from 'src/Libs/flow/end-user-settings/wallbox'
import { getUuid, logError } from 'src/Libs/flow/utils'
import { stripUnchangedValues } from 'src/Libs/form/utils'
import { AuthFetchWithResponse } from 'src/Types/api'
import {
  getEebusPOSTDto,
  getEebusPUTDto,
  getModbusTcpPOSTDto,
  getModbusTcpPUTDto,
  getMyPvPUTDto,
  getStorageName,
  setIsEvseExpandedToCache,
} from '../../../parsers'
import {
  EebusChargingStation,
  Emc,
  GeneralSettings,
  HeatpumpIo,
  Htc320hIo,
  InstallationFormModel,
  IoInterfaces,
  ModbusTcpChargingStation,
  MyPvIo,
  Storage,
  StorageIo,
  StorageSystem,
} from '../../forms'
import { getTreatHtcAsNonSgReady } from '../../utils'
import { updateEmcIdlePeriods, updateInstallationConfig } from '../Installation-config'
import { updateEmcConfig, updateStorageConfig } from '../device-configs'
import { createEmcDeviceLink, createStorageDeviceLink, updateDeviceLinkPairingId } from '../device-link'
import { createInstallation, InstallationPOSTorPUTDto, updateInstallation } from '../installations'
import {
  createEvse,
  createHeatpumpIo,
  createHtc320hIo,
  createMeter,
  createMyPvIo,
  createStorageIo,
  pairEebusEvse,
  pairHeatpumpIo,
  pairModbusTcpEvse,
  updateHeatpumpIo,
  updateHtc320hIo,
  updateMeter,
  updateModbusTcpEvseOcppPassword,
  updateMyPvIo,
  updateStorageIo,
} from '../sub-devices'
import {
  getAreBlockingWeeklySchedulesDirty,
  getIsEebusDirty,
  getIsEmcConfigDirty,
  getIsHeatpumpIoDirty,
  getIsHtc320hIoDirty,
  getIsInstallationConfigDirty,
  getIsInstallationDirty,
  getIsMeterDirty,
  getIsModbusTcpDirty,
  getIsMyPvIoDirty,
  getIsStorageConfigDirty,
  getIsStorageIoDirty,
  getShouldUpdateEmcPairingId,
  getShouldUpdateHtc320hIoNominalPower,
  getShouldUpdateStoragePairingId,
} from './get-dirty-state'
import {
  getEmcConfigPUTDto,
  getEmcIdlePeriodsPUTDto,
  getInstallationConfigPUTDto,
  getInstallationPOSTorPUTDto,
  getMeterPOSTorPUTDto,
  getStorageConfigPUTDto,
} from './get-installation-payloads'

/** Gets form data and creates the requests to save a new installation. */
export const getInstallationRequests = (
  installation: InstallationFormModel,
  installationState: FormState<InstallationFormModel>,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): InstallationRequestBatch => {
  const isNewInstallation = installation.id === null
  const installationIdRequest: InstallationRequestBatch['installationIdRequest'] =
    installation.id === null
      ? {
          message: installation.name,
          installationId: null,
          installationRequest: () =>
            createInstallation(getInstallationPOSTorPUTDto(installation, true, true) as InstallationPOSTorPUTDto, authFetch).then(
              res => res.value || 0
            ),
        }
      : {
          message: installation.name,
          installationId: installation.id,
          installationRequest: null,
        }

  const installationRequests: InstallationRequestBatch['installationRequests'] = []

  //#region Basic Requests
  if (!isNewInstallation) {
    // Always send a PUT request (except for new installations) so that "needToBeSynchronize" is set to true when a device changes.
    installationRequests.push({
      uid: getUuid(),
      message: t('installation.save.generalDataSaving'),
      request: (installationId: number) =>
        updateInstallation(installationId, getInstallationPOSTorPUTDto(installation, true, getIsInstallationDirty(installationState)), authFetch),
    })
  }

  if (getIsInstallationConfigDirty(installationState) || isNewInstallation) {
    installationRequests.push({
      uid: getUuid(),
      message: t('installation.save.configSaving'),
      request: (installationId: number) => updateInstallationConfig(installationId, getInstallationConfigPUTDto(installation), authFetch),
    })
  }

  const requestBase: RequestsBase = { t, authFetch, installationState }

  const emc = installation.emcDevice

  /** Can't proceed withought EMC. The EMC's device id is required. */
  if (emc === null)
    return {
      installationIdRequest,
      installationRequests,
      emcIdRequest: null,
      emcRequests: [],
      allStorageRequests: [],
      controlledSubDeviceRequests: [],
      meterSubDeviceRequests: [],
      controllerSubDeviceRequests: [],
    }
  //#endregion

  //#region Additional Settings
  const {
    storageDevices,
    storageSystem,
    generalSettings,
    emcDevice,
    meters,
    heatpump,
    ioInterfaces,
    chargingStations,
    chargingStationsExpandedState,
  } = installation

  /** EMC */
  const { emcIdRequest, emcRequests } = getEmcRequests(emc, generalSettings, emcDevice, requestBase)

  /** Storage */
  const allStorageRequests: InstallationRequestBatch['allStorageRequests'] = []

  storageDevices.forEach((storageDevice, storageIndex) => {
    const { storageIdRequest, storageRequests } = getStorageRequests(storageDevice, storageSystem, storageDevice?.name, storageIndex, requestBase)

    allStorageRequests.push({
      storageIdRequest,
      storageRequests,
    })
  })

  //#region SubDevices

  /** Controlled (Evse) */
  const modbusTcpDevices: ModbusTcpChargingStation[] = chargingStations.modbusTcp
  const expansionModbus = chargingStationsExpandedState.modbusTcp

  const eebusTcpDevices: EebusChargingStation[] = chargingStations.eebus
  const expansionEebus = chargingStationsExpandedState.eebus

  /** Handle Evse (Controlled) devices */
  const controlledSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'] = getControlledSubdeviceRequests(
    modbusTcpDevices,
    expansionModbus,
    eebusTcpDevices,
    expansionEebus,
    requestBase
  )

  /** Meters */
  const meterSubDeviceRequests: InstallationRequestBatch['meterSubDeviceRequests'] = getMetersRequests(meters, requestBase)

  /** Controller (I/O) */

  const controllerSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'] = getControllerRequests(
    heatpump,
    ioInterfaces,
    generalSettings,
    requestBase
  )

  //#endregion

  //#endregion

  return {
    installationIdRequest,
    installationRequests,
    emcIdRequest,
    emcRequests,
    allStorageRequests,
    controlledSubDeviceRequests,
    meterSubDeviceRequests,
    controllerSubDeviceRequests,
  }
}
//#region Request-Build-Helper

//#endregion
interface RequestsBase {
  authFetch: AuthFetchWithResponse
  installationState: FormState<InstallationFormModel>
  t: TFunction
}
//#region Evse helper
type ModbusTcpEvseAfterRequestParams = {
  modbusTcp: ModbusTcpChargingStation
  installationId: number
  emcId: string
  modbusTcpId: string
  authFetch: AuthFetchWithResponse
  wbType: string | null
  offline: boolean
  ip: string
  macOriginVerified: boolean
}

const getModbusTcpEvsePostAfterRequests = (
  params: ModbusTcpEvseAfterRequestParams
): Promise<void | [ApiResult<null> | undefined, ApiResult<null> | undefined]> => {
  const pairingRequest = getModbusTcpEvsePairingRequest(params)
  const passwordUpdateRequest = getWittyPlusPasswordUpdateRequest(params)

  return Promise.all([pairingRequest, passwordUpdateRequest]).catch(error => {
    logError(error)
  })
}

const getModbusTcpEvsePairingRequest = ({
  modbusTcp,
  installationId,
  emcId,
  modbusTcpId,
  authFetch,
  wbType,
  macOriginVerified,
}: ModbusTcpEvseAfterRequestParams): Promise<ApiResult<null>> | undefined => {
  if (modbusTcp.initialPairingId === modbusTcp.pairingId) return

  return pairModbusTcpEvse(installationId, emcId, modbusTcpId, modbusTcp.pairingId, wbType, macOriginVerified, authFetch)
}

const getWittyPlusPasswordUpdateRequest = ({
  modbusTcp,
  installationId,
  emcId,
  modbusTcpId,
  authFetch,
}: ModbusTcpEvseAfterRequestParams): Promise<ApiResult<null>> | undefined => {
  if (!modbusTcp.carAtHomeBehavior.isEnabled) return
  if (!modbusTcp.carAtHomeBehavior.isCredentialsActive) return
  if (!modbusTcp.carAtHomeBehavior.password) return

  return updateModbusTcpEvseOcppPassword(installationId, emcId, modbusTcpId, modbusTcp.carAtHomeBehavior.password, authFetch)
}
//#endregion

//#region EMCRequests
const getEmcRequests = (
  emc: Emc,
  generalSettings: GeneralSettings,
  emcDevice: Emc | null,
  { t, authFetch, installationState }: RequestsBase
): { emcIdRequest: InstallationRequestBatch['emcIdRequest']; emcRequests: InstallationRequestBatch['emcRequests'] } => {
  const isNewEmc = emc.isNew
  const emcIdRequest: InstallationRequestBatch['emcIdRequest'] = isNewEmc
    ? {
        message: `EMC ${t('installation.save.deviceLinkBaseInfoSaving')}`,
        emcId: null,
        emcRequest: (installationId: number) => createEmcDeviceLink(installationId, emc.pairingId, authFetch).then(res => res.value ?? 0),
      }
    : {
        message: `EMC ${t('installation.save.deviceLinkBaseInfoSaving')}`,
        emcId: emc.deviceId,
        emcRequest: null,
      }

  const emcRequests: InstallationRequestBatch['emcRequests'] = []

  if (isNewEmc || getIsEmcConfigDirty(installationState)) {
    emcRequests.push({
      uid: getUuid(),
      message: `EMC ${t('installation.save.deviceLinkContentSaving')}`,
      request: (installationId: number, emcId: string) => updateEmcConfig(installationId, emcId, getEmcConfigPUTDto(generalSettings), authFetch),
    })
  }

  if (!isNewEmc && getShouldUpdateEmcPairingId(emcDevice)) {
    emcRequests.push({
      uid: getUuid(),
      message: `EMC - ${t('installation.save.deviceLinkPairingSaving')}`,
      request: (installationId: number, emcId: string) =>
        updateDeviceLinkPairingId(installationId, emcId, emc.initialPairingId, emc.pairingId, authFetch),
    })
  }
  return { emcIdRequest, emcRequests }
}
//#endregion

//#region StorageRequests
const getStorageRequests = (
  storage: Storage | null,
  storageSystem: StorageSystem,
  storageDeviceName: string | undefined,
  storageIndex: number,
  { t, authFetch, installationState }: RequestsBase
): {
  storageIdRequest: InstallationRequestBatch['allStorageRequests'][number]['storageIdRequest']
  storageRequests: InstallationRequestBatch['allStorageRequests'][number]['storageRequests']
} => {
  const isNewStorage = !!storage?.isNew
  const storageSpecificSettings: StorageSystem['storageSpecificSettings'][number] | undefined = storageSystem.storageSpecificSettings[storageIndex]

  const storageIdRequest: InstallationRequestBatch['allStorageRequests'][number]['storageIdRequest'] = (() => {
    if (!storage) return null

    if (isNewStorage)
      return {
        message: `${storage?.name}: ${t('installation.types.storage')} ${t('installation.save.deviceLinkBaseInfoSaving')}`,
        storageId: null,
        storageRequest: (installationId: number) =>
          createStorageDeviceLink(installationId, getStorageName(storage?.name, t), storage.pairingId, authFetch).then(
            newStorageId => newStorageId || 0
          ),
      }

    return {
      message: `${t('installation.types.storage')} ${t('installation.save.deviceLinkBaseInfoSaving')}`,
      storageId: storage.deviceId,
      storageRequest: null,
    }
  })()
  const storageRequests: InstallationRequestBatch['allStorageRequests'][number]['storageRequests'] = []

  if (storageSpecificSettings && (isNewStorage || getIsStorageConfigDirty(installationState, storageIndex))) {
    const configData = isNewStorage
      ? storageSpecificSettings
      : stripUnchangedValues(storageSpecificSettings, installationState.dirtyFields.storageSystem?.storageSpecificSettings?.[storageIndex] || {})

    storageRequests.push({
      uid: getUuid(),
      message: `${storage?.name}: ${t('installation.types.storage')} ${t('installation.save.deviceLinkContentSaving')}`,
      request: (installationId: number, storageId: string) => {
        return updateStorageConfig(installationId, storageId, getStorageConfigPUTDto(configData, storageDeviceName, t), authFetch).then(result => {
          if (result.hasErrors) throw result.error ?? new Error('Failed to update storage config')
          return result
        })
      },
    })
  }

  if (!isNewStorage && storage && getShouldUpdateStoragePairingId(storage)) {
    storageRequests.push({
      uid: getUuid(),
      message: t('installation.save.deviceLinkPairingSaving'),
      request: (installationId: number, storageId: string) =>
        updateDeviceLinkPairingId(installationId, storageId, storage.initialPairingId, storage.pairingId, authFetch),
    })
  }

  if (getAreBlockingWeeklySchedulesDirty(installationState)) {
    storageRequests.push({
      uid: getUuid(),
      message: t('installation.save.idlePeriodsSaving'),
      request: (installationId: number) => updateEmcIdlePeriods(installationId, getEmcIdlePeriodsPUTDto(storageSystem), authFetch),
    })
  }
  return { storageIdRequest, storageRequests }
}
//#endregion

//#region Controlled Devices Requests
const getControlledSubdeviceRequests = (
  modbusTcpDevices: ModbusTcpChargingStation[],
  expansionModbus: { [id: string]: boolean },
  eebusTcpDevices: EebusChargingStation[],
  expansionEebus: { [id: string]: boolean },
  { t, authFetch, installationState }: RequestsBase
): InstallationRequestBatch['controllerSubDeviceRequests'] => {
  const controlledSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'] = []

  //#region ModBus
  for (const [index, modbusTcp] of modbusTcpDevices.entries()) {
    if (modbusTcp.isNew) {
      controlledSubDeviceRequests.push({
        uid: getUuid(),
        message: `${modbusTcp.name} ${t('installation.save.controlledSaving')}`,
        request: (installationId: number, emcId: string) =>
          createEvse(installationId, emcId, getModbusTcpPOSTDto(modbusTcp), authFetch)
            .then(res => res.value)
            .then(newModbusTcpDeviceId => {
              if (!newModbusTcpDeviceId) return
              // Set the newly created evse's expanded state to cache
              const isExpanded = !!expansionModbus[modbusTcp.id]
              setIsEvseExpandedToCache(newModbusTcpDeviceId, isExpanded)

              return getModbusTcpEvsePostAfterRequests({
                modbusTcp,
                installationId,
                emcId,
                modbusTcpId: newModbusTcpDeviceId,
                wbType: getModbusTcpPOSTDto(modbusTcp).evseSubTypeParameters.wbType,
                authFetch,
                offline: true,
                ip: '',
                macOriginVerified: modbusTcp.macOriginVerified ?? false,
              })
            }),
      })
    } else if (getIsModbusTcpDirty(installationState, index)) {
      controlledSubDeviceRequests.push({
        uid: getUuid(),
        message: `${modbusTcp.name} ${t('installation.save.controlledSaving')}`,
        request: (installationId: number, emcId: string) =>
          updateEvse(installationId, emcId, modbusTcp.deviceId, getModbusTcpPUTDto(modbusTcp), authFetch).then(() => {
            return getModbusTcpEvsePostAfterRequests({
              modbusTcp,
              installationId,
              emcId,
              modbusTcpId: modbusTcp.deviceId,
              authFetch,
              wbType: getModbusTcpPUTDto(modbusTcp).evseSubTypeParameters.wbType,
              offline: true,
              ip: '',
              macOriginVerified: modbusTcp.macOriginVerified ?? false,
            })
          }),
      })
    }
  }
  //#endregion

  //#region Eebus
  for (const [index, eebus] of eebusTcpDevices.entries()) {
    if (eebus.isNew) {
      controlledSubDeviceRequests.push({
        uid: getUuid(),
        message: `${eebus.name} ${t('installation.save.controlledSaving')}`,
        request: (installationId: number, emcId: string) =>
          createEvse(installationId, emcId, getEebusPOSTDto(eebus), authFetch)
            .then(res => res.value)
            .then(newEebusDeviceId => {
              if (!newEebusDeviceId) return
              // Set the newly created evse's expanded state to cache
              const isExpanded = !!expansionEebus[eebus.id]
              setIsEvseExpandedToCache(newEebusDeviceId, isExpanded)

              if (!eebus.pairingId) return
              return pairEebusEvse(installationId, emcId, newEebusDeviceId, eebus.pairingId, authFetch)
            }),
      })
    } else if (getIsEebusDirty(installationState, index)) {
      controlledSubDeviceRequests.push({
        uid: getUuid(),
        message: `${eebus.name} ${t('installation.save.controlledSaving')}`,
        request: (installationId: number, emcId: string) =>
          updateEvse(installationId, emcId, eebus.deviceId, getEebusPUTDto(eebus), authFetch).then(() => {
            if (eebus.initialPairingId === eebus.pairingId) return
            return pairEebusEvse(installationId, emcId, eebus.deviceId, eebus.pairingId, authFetch)
          }),
      })
    }
  }
  //#endregion

  return controlledSubDeviceRequests
}
//#endregion

//#region MetersRequests
const getMetersRequests = (
  meters: Meter[],
  { t, authFetch, installationState }: RequestsBase
): InstallationRequestBatch['meterSubDeviceRequests'] => {
  const meterSubDeviceRequests: InstallationRequestBatch['meterSubDeviceRequests'] = []

  for (const [index, meter] of meters.entries()) {
    if (meter.isNew) {
      meterSubDeviceRequests.push({
        uid: getUuid(),
        message: `${meter.name} ${t('installation.save.meterSaving')}`,
        request: (installationId: number, emcId: string) => createMeter(installationId, emcId, getMeterPOSTorPUTDto(meter), authFetch),
      })
    } else if (getIsMeterDirty(installationState, index)) {
      meterSubDeviceRequests.push({
        uid: getUuid(),
        message: `${meter.name} ${t('installation.save.meterSaving')}`,
        request: (installationId: number, emcId: string) =>
          updateMeter(installationId, emcId, meter.deviceId, getMeterPOSTorPUTDto(meter), authFetch),
      })
    }
  }

  return meterSubDeviceRequests
}
//#endregion

//#region ControllerRequests
const getControllerRequests = (
  heatpump: HeatpumpIo | null,
  ioInterfaces: IoInterfaces,
  generalSettings: GeneralSettings,
  requestBase: RequestsBase
): InstallationRequestBatch['controllerSubDeviceRequests'] => {
  const { htc320h, storage, mypv } = ioInterfaces
  const controllerSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'] = []

  //#region HeatPump
  if (heatpump !== null) {
    addHeatpumpToRequests(heatpump, controllerSubDeviceRequests, requestBase)
  }
  //#endregion

  //#region HTC320
  if (htc320h !== null) {
    addHTCToRequests(htc320h, generalSettings, storage, heatpump, controllerSubDeviceRequests, requestBase)
  }
  //#endregion

  //#region Storage
  if (storage !== null) {
    addStorageToRequests(storage, controllerSubDeviceRequests, requestBase)
  }
  //#endregion

  //#region MyPv
  if (mypv !== null) {
    addMyPvToRequests(mypv, controllerSubDeviceRequests, requestBase)
  }
  //#endregion

  return controllerSubDeviceRequests
}

//#region Controller Helpers
const addHeatpumpToRequests = (
  heatpump: HeatpumpIo,
  controllerSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'],
  { t, authFetch, installationState }: RequestsBase
): void => {
  if (heatpump.isNew) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${heatpump.name} ${t('installation.save.heatpumps')}`,
      request: (installationId: number, emcId: string) =>
        createHeatpumpIo(installationId, emcId, heatpump, authFetch)
          .then(res => res.value)
          .then(heatpumpId => {
            if (!heatpump.pairingId || heatpumpId === null) return
            return pairHeatpumpIo(installationId, emcId, heatpumpId.toString(), heatpump.pairingId, authFetch)
          }),
    })
  } else if (getIsHeatpumpIoDirty(installationState)) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${heatpump.name} ${t('installation.save.heatpumps')}`,
      request: (installationId: number, emcId: string) =>
        updateHeatpumpIo(installationId, emcId, heatpump.deviceId, heatpump, authFetch).then(() => {
          if (heatpump.pairingId === heatpump.initialPairingId) return
          return pairHeatpumpIo(installationId, emcId, heatpump.deviceId, heatpump.pairingId, authFetch)
        }),
    })
  }
}

const addMyPvToRequests = (
  mypv: MyPvIo,
  controllerSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'],
  { t, authFetch, installationState }: RequestsBase
): void => {
  if (mypv.isNew) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${mypv.name} ${t('installation.save.controllerSaving')}`,
      request: (installationId: number, emcId: string) => createMyPvIo(installationId, emcId, mypv, authFetch),
    })
  } else if (getIsMyPvIoDirty(installationState)) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${mypv.name} ${t('installation.save.controllerSaving')}`,
      request: (installationId: number, emcId: string) => updateMyPvIo(installationId, emcId, mypv.deviceId, getMyPvPUTDto(mypv), authFetch),
    })
  }
}

const addStorageToRequests = (
  storage: StorageIo,
  controllerSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'],
  { t, authFetch, installationState }: RequestsBase
): void => {
  if (storage.isNew) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${storage.name} ${t('installation.save.controllerSaving')}`,
      request: (installationId: number, emcId: string) =>
        createStorageIo(
          installationId,
          emcId,
          {
            deviceName: storage.name,
            nominalPower: storage.nominalPower || 0,
            mediaParameters: {
              deviceLinkId: storage.storageDeviceLinkId.toString(),
            },
          },
          authFetch
        ),
    })
  } else if (getIsStorageIoDirty(installationState)) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${storage.name} ${t('installation.save.controllerSaving')}`,
      request: (installationId: number, emcId: string) =>
        updateStorageIo(
          installationId,
          emcId,
          storage.deviceId,
          {
            deviceName: storage.name,
            nominalPower: storage.nominalPower || 0,
          },
          authFetch
        ),
    })
  }
}

const addHTCToRequests = (
  htc320h: Htc320hIo,
  generalSettings: GeneralSettings,
  storage: StorageIo | null,
  heatpump: HeatpumpIo | null,
  controllerSubDeviceRequests: InstallationRequestBatch['controllerSubDeviceRequests'],
  { t, authFetch, installationState }: RequestsBase
): void => {
  const shouldUpdateHtc320hIoNominalPower = getShouldUpdateHtc320hIoNominalPower(htc320h, generalSettings, storage, heatpump)
  const treatHtcAsNonSgReady = getTreatHtcAsNonSgReady(generalSettings, storage, heatpump)

  if (htc320h.isNew) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${htc320h.name} ${t('installation.save.controllerSaving')}`,
      request: (installationId: number, emcId: string) =>
        createHtc320hIo(installationId, emcId, htc320h, treatHtcAsNonSgReady, authFetch).then(res => res.value),
    })
  } else if (getIsHtc320hIoDirty(installationState) || shouldUpdateHtc320hIoNominalPower) {
    controllerSubDeviceRequests.push({
      uid: getUuid(),
      message: `${htc320h.name} ${t('installation.save.controllerSaving')}`,
      request: (installationId: number, emcId: string) =>
        updateHtc320hIo(installationId, emcId, htc320h.deviceId, htc320h, treatHtcAsNonSgReady, authFetch),
    })
  }
}
//#endregion

//#endregion
